import React, { useCallback, useRef, useState } from 'react'
import { useLocale } from 'antd/es/locale'
import defaultLocale from 'antd/es/locale/en_US'

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from 'src/components/ui/alert-dialog'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from 'src/components/ui/popover'
import { ExclamationCircleFilled } from 'src/icons/antd-compat'

import type { SeamPopconfirmProps } from './props'
import type { ButtonProps } from '../antd-props'
import { Button } from '../Button.shadcn'
import {
  AntdPlacement,
  omitTransient,
  toRadixSideAlign,
} from '../overlay/placement'
import { useStableId } from '../overlay/useStableId'

/* -------------------------------------------------------------------------
 * The shadcn half of the `Popconfirm` seam. TWO shapes, one state machine.
 *
 * ---------------------------------------------------------------------------
 * WHY TWO SHAPES (PRO-736.16, captain's ruling)
 *
 * antd's Popconfirm is a small popover anchored to its trigger; shadcn's
 * AlertDialog is a centred modal that takes the screen. Converting all 59 call
 * sites to the modal would have been a real interaction change on 41 per-row
 * confirmations — a table row's "Remove?" does not deserve a takeover, and
 * three call sites here carry in-code comments recording that the anchored
 * shape was specifically ASKED FOR by the designer.
 *
 * The captain's rule, and the one to apply when adding a call site: a row
 * delete and a page-level "delete this entire thing" are not the same act, so
 * ask HOW MUCH THE USER LOSES IF IT FIRES BY MISTAKE and pick the shape from
 * the answer — never from where the component happens to sit in the DOM.
 *
 *   • little or nothing lost, or trivially reversible  → the anchored popover
 *     (the DEFAULT; no prop, and every existing call site keeps its shape)
 *   • unrecoverable, or destructive at scale           → `modal`
 *
 * `modal` is this seam's OWN prop, not antd's — the same kind of addition
 * `../Menu`'s `sidebar` is. With the toggle OFF it is stripped in the
 * dispatcher and never reaches antd, so it changes nothing for the customers
 * on the antd rendering.
 *
 * ---------------------------------------------------------------------------
 * THE SAFETY PROPERTY, which is the whole point of this component
 *
 * A STRAY DISMISS MUST NEVER CONFIRM. Escape, an outside/backdrop click, a
 * focus that leaves the popover and the Cancel button all CLOSE and none of
 * them reaches `onConfirm`. Only the OK button does. That is asserted directly
 * in `../__tests__/popconfirm-dismiss-never-confirms.test.tsx`, for BOTH
 * shapes — an anchored popover dismissing on outside click is exactly where
 * that bug would hide, so the popover shape is tested first, not second.
 *
 * Two deliberate divergences from antd, both in the SAFER direction, both
 * confined to the shadcn rendering:
 *
 *  • THE MODAL SHAPE DOES NOT DISMISS ON A BACKDROP CLICK. antd's Popconfirm
 *    does (it is a popover), and antd's own `Modal.confirm` does not. Radix's
 *    AlertDialog is the primitive with the second behaviour, and on the sites
 *    that opt into `modal` — unrecoverable acts — making the user choose OK or
 *    Cancel is the right trade. Escape still cancels.
 *
 *    HOW MANY SITES THAT IS, is not stated here. The count lives in exactly
 *    one place — `MODAL_SITES` in
 *    `../__tests__/popconfirm-shape-inventory.test.ts`, which is executable and
 *    fails when it drifts. Prose repeating it goes stale silently, and did.
 *  • FOCUS LANDS ON CANCEL, not on the panel. antd's Popconfirm autofocuses
 *    nothing at all (`ActionButton` is never handed `autoFocus`), so a keyboard
 *    user arrives with focus still on the trigger and Enter re-opens rather
 *    than answering. Cancel is the safe default answer for a confirmation, so
 *    it is where focus goes whenever a Cancel button is shown.
 *
 * Focus RETURNS to the trigger on close in both shapes. Radix restores to its
 * own `Trigger`, and this seam has none — it anchors with `PopoverAnchor` and
 * drives `open` itself, exactly as the Select and Menu seams do — so the node
 * is captured on open and refocused by hand.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE POPOVER SHAPE IS *NOT*: Radix-modal.
 *
 * `modal={false}` on the Radix Popover is a decision, not the default falling
 * through. Radix's modal popover mounts `react-remove-scroll`, which removes
 * the page's scrollbar for as long as the popup is open — on a dense table,
 * opening a row confirm would shift the whole page sideways. So the popover
 * shape leaves the page live and leans on Radix's dismissable layer instead:
 * an outside pointer-down closes it, and so does a focus that leaves it (which
 * is what makes Tab-out a dismissal rather than an escape from the keyboard
 * path). Focus is genuinely TRAPPED only in the `modal` shape, which is the
 * shape whose acts justify a trap.
 *
 * ---------------------------------------------------------------------------
 * THE OK BUTTON IS antd's `ActionButton`, transcribed
 *
 * `antd/es/_util/ActionButton` is what antd's Popconfirm drives OK with, under
 * `emitEvent: true` + `quitOnNullishReturnValue: true`. Its contract is load-
 * bearing here: ~50 of the 59 call sites hand `onConfirm` a promise (an async
 * handler, or an Apollo `useMutation` returned straight out of an arrow), and
 * they rely on the popup staying OPEN with the OK button spinning until it
 * settles, and on a REJECTION leaving the popup open so the user can retry.
 * `runConfirm` below is that logic written out rather than imported, because
 * `_util` vanishes with the package. Read antd 5.15.4's source beside it if
 * you change it — the double-click guard (`pendingRef`) is part of the
 * contract, not defensive coding.
 *
 * A call site's own `okButtonProps.loading` still WINS over the internal
 * spinner, because antd spreads `buttonProps` after its own `loading` and 10
 * call sites here drive a row-scoped `loading` of their own.
 * ------------------------------------------------------------------------- */

const isThenable = (v: unknown): v is PromiseLike<unknown> =>
  !!v && typeof (v as PromiseLike<unknown>).then === 'function'

type ConfirmEvent = React.MouseEvent<HTMLElement>

/** The message block — identical in both shapes, so it is written once. */
const Message = ({
  icon,
  title,
  description,
  titleId,
  descriptionId,
  renderTitle,
  renderDescription,
}: {
  icon: React.ReactNode
  title: React.ReactNode
  description: React.ReactNode
  titleId: string
  descriptionId: string
  renderTitle: (node: React.ReactNode, id: string) => JSX.Element
  renderDescription: (node: React.ReactNode, id: string) => JSX.Element
}) => (
  <div data-slot="popconfirm-message" className="flex gap-2">
    {icon != null && (
      <span
        data-slot="popconfirm-icon"
        // `--color-wh-gold-500`, the bridge `src/styles/tailwind.css` carries
        // for antd's status hues (added for `../Alert`, which paints its
        // warning with the same token). A hex literal here would be
        // theme-FROZEN — antd gold on a dark panel whose every other colour
        // flips. See AGENTS.md's colour-literal entry.
        className="mt-0.5 shrink-0 text-[14px] leading-none text-wh-gold-500"
      >
        {icon}
      </span>
    )}
    <div className="min-w-0 flex-1">
      {title != null && title !== '' && renderTitle(title, titleId)}
      {description != null && description !== '' && (
        <div className="mt-1">
          {renderDescription(description, descriptionId)}
        </div>
      )}
    </div>
  </div>
)

export const PopconfirmShadcn: React.FC<SeamPopconfirmProps> = (props) => {
  const {
    children,
    title,
    description,
    icon = <ExclamationCircleFilled />,
    okText,
    cancelText,
    okType = 'primary',
    okButtonProps,
    cancelButtonProps,
    showCancel = true,
    disabled = false,
    modal = false,
    placement,
    open: openProp,
    visible: visibleProp,
    defaultOpen,
    defaultVisible,
    onConfirm,
    onCancel,
    onOpenChange,
    onVisibleChange,
    onPopupClick,
    getPopupContainer,
    overlayClassName,
    overlayStyle,
    className,
    // accepted, then dropped so none of them reaches the DOM. Every one is an
    // antd/rc-trigger positioning or animation knob with no Radix counterpart;
    // no call site in this repo passes any of them to a Popconfirm.
    trigger: _trigger,
    arrow: _arrow,
    autoAdjustOverflow: _autoAdjustOverflow,
    destroyTooltipOnHide: _destroyTooltipOnHide,
    mouseEnterDelay: _mouseEnterDelay,
    mouseLeaveDelay: _mouseLeaveDelay,
    transitionName: _transitionName,
    align: _align,
    zIndex: _zIndex,
    prefixCls: _prefixCls,
    ...rest
  } = props

  const [localeConfig] = useLocale('Popconfirm', defaultLocale.Popconfirm)
  const ids = useStableId('popconfirm')
  const titleId = `${ids}-title`
  const descriptionId = `${ids}-description`
  const hasDescription = description != null && description !== ''

  // Merged state, written out rather than taken from `rc-util`'s
  // `useMergedState`: that module goes away with antd, and the rule here is
  // small enough to state — `open` wins, then the v4 `visible` alias, then the
  // component's own state seeded from `defaultOpen`/`defaultVisible`.
  const controlledOpen = openProp ?? visibleProp
  const controlled = controlledOpen !== undefined
  const [internalOpen, setInternalOpen] = useState(
    () => defaultOpen ?? defaultVisible ?? false
  )
  const open = controlled ? Boolean(controlledOpen) : internalOpen

  const [pending, setPending] = useState(false)
  /**
   * THE DOUBLE-CLICK GUARD, AND NOTHING ELSE. It exists so a second OK press
   * cannot fire `onConfirm` twice while the first is still in flight, and it
   * must guard `runConfirm` ONLY.
   *
   * It must NOT gate any DISMISSAL. Twice in this port an early-return on this
   * latch trapped the panel open with no way out — once through a handler that
   * threw synchronously, once through a promise that never settled — which on
   * a destructive confirm is a divergence in the unsafe direction, and on the
   * modal shape leaves the page behind a focus-trapping backdrop. antd does
   * neither: its `ActionButton` leaves Cancel live while a confirm is pending
   * and rc-tooltip still closes on an outside click.
   *
   * The readers were enumerated: `dismiss`, `handleCancel` and `runConfirm`.
   * Only `runConfirm` reads it as a guard now; the other two only RECORD, via
   * `dismissedWhilePendingRef`, that the user left — so the settle handlers
   * below can skip re-closing a panel that is already closed and skip pulling
   * focus back to a trigger the user has moved on from. Do not re-add an
   * early-return on this ref to a dismissal path.
   *
   * That enumeration is the WHOLE audit, not two of three doors closed —
   * asked, because this latch had already trapped the panel twice by different
   * routes. The two OTHER ways the panel can close never consulted this ref
   * and are each suppressed for a reason of their own, so neither was ever an
   * escape hatch either: Radix's own `onOpenChange` cannot fire, because every
   * dismissal handler below calls `preventDefault()`, and a second click on
   * the trigger is absorbed by `reopenGuardRef`. A CONTROLLED call site
   * driving its own `open` prop is the remaining route, and it is external.
   *
   * Worth stating plainly, because it surprises people: closing the panel does
   * NOT abort a mutation already in flight. During a pending confirm "Cancel"
   * dismisses the DIALOG, not the ACTION. That is antd's behaviour today, so
   * matching it introduces no new hazard; making a running delete genuinely
   * uninterruptible would be a product decision about mutations, not about
   * this seam.
   */
  const pendingRef = useRef(false)
  /** Set by a dismissal that happened while `pendingRef` was held. */
  const dismissedWhilePendingRef = useRef(false)
  /**
   * Whether the CLOSE that is about to happen should pull focus back to the
   * trigger. True for the deliberate ways out — Escape, Cancel, confirm — and
   * false when focus or a pointer simply LEFT the panel, because the user has
   * already sent focus onward. Radix's close-auto-focus is the single place
   * focus is restored, so the flag has to be read there and not only in
   * `dismiss`.
   */
  const restoreFocusOnCloseRef = useRef(true)
  // Captured on open, before Radix's mount-autofocus moves focus into the
  // panel, so it is the node the user was on when the confirm opened.
  const restoreFocusRef = useRef<HTMLElement | null>(null)
  const [anchorNode, setAnchorNode] = useState<HTMLElement | null>(null)
  const triggerWrapperRef = useRef<HTMLSpanElement | null>(null)
  // Set when a pointer-down ON THE TRIGGER dismissed an open panel, and read by
  // the click that follows it. See `openFromTrigger`.
  const reopenGuardRef = useRef(false)

  /**
   * The anchor is resolved FROM THE DOM, not from a ref handed to the child.
   *
   * `<PopoverAnchor asChild ref={…}>` only ever resolves a node when the child
   * forwards its ref to a DOM element, and most children here do not: the seam
   * `../Tooltip`'s shadcn half deliberately drops the ref, and `ButtonDelete`
   * (`src/storybook/components/Buttons`) is a plain function component, which
   * silently takes none. With no anchor Radix Popper has no reference element,
   * so the panel rendered unpositioned in the viewport's top-left corner — and
   * `getPopupContainer` resolved to `undefined`, quietly sending three
   * route-studio popups to the body instead of the container they asked for.
   *
   * Reading `firstElementChild` off the wrapper asks nothing of the child at
   * all. It is re-resolved whenever `children` changes rather than only in the
   * wrapper's ref callback, because a child can swap its root ELEMENT without
   * the wrapper remounting — `../Tooltip` renders `<>{children}</>` when its
   * title is falsy and a full Radix tree when it is not, so the button is a
   * different DOM node on either side of that — and a ref callback does not
   * re-fire for it. `children` is a fresh JSX element on every parent render,
   * which is precisely the set of commits in which the child can have changed.
   * `setAnchorNode` returns the previous node when it is unchanged, so React
   * bails out and the pass costs one comparison.
   */
  React.useEffect(() => {
    const node =
      (triggerWrapperRef.current?.firstElementChild as HTMLElement | null) ??
      null
    setAnchorNode((previous) => (previous === node ? previous : node))
  }, [children])

  const settingOpen = useCallback(
    (next: boolean, event?: ConfirmEvent) => {
      if (!controlled) setInternalOpen(next)
      onVisibleChange?.(next)
      onOpenChange?.(next, event)
    },
    [controlled, onOpenChange, onVisibleChange]
  )

  /**
   * A dismissal: closes, restores focus, and NEVER reaches `onConfirm`.
   * `onCancel` is deliberately not called either — antd's Escape and outside
   * click do not call it, only its Cancel button does, and 1 call site
   * (ViewDashboards) uses `onCancel` purely to stop the click propagating to
   * the card underneath.
   */
  const dismiss = useCallback(
    (event?: ConfirmEvent, restoreFocus = true) => {
      if (pendingRef.current) dismissedWhilePendingRef.current = true
      restoreFocusOnCloseRef.current = restoreFocus
      settingOpen(false, event)
      if (restoreFocus) restoreFocusRef.current?.focus()
    },
    [settingOpen]
  )

  const handleCancel = useCallback(
    (event: ConfirmEvent) => {
      if (pendingRef.current) dismissedWhilePendingRef.current = true
      restoreFocusOnCloseRef.current = true
      settingOpen(false, event)
      onCancel?.(event)
      restoreFocusRef.current?.focus()
    },
    [onCancel, settingOpen]
  )

  /** antd's `ActionButton` under `emitEvent` + `quitOnNullishReturnValue`. */
  const runConfirm = useCallback(
    (event: ConfirmEvent) => {
      if (pendingRef.current) return
      pendingRef.current = true
      dismissedWhilePendingRef.current = false

      let result: unknown
      try {
        result = onConfirm?.(event)
      } catch (error) {
        // A handler that throws SYNCHRONOUSLY must not latch the panel open.
        // `pendingRef` is the double-click guard, and `dismiss`, `handleCancel`
        // and `runConfirm` all early-return while it is set — so leaving it
        // true after a throw makes the confirmation impossible to cancel, to
        // retry, or to escape from until the component remounts. On a panel
        // guarding a destructive act that is a divergence in the UNSAFE
        // direction: antd's `ActionButton` sets its `loading` only in the
        // promise branch and has no such latch.
        //
        // Released HERE rather than in a `finally`, which would look tidier and
        // be wrong: a `finally` runs the moment `onConfirm` RETURNS, so it
        // would also release the guard on the promise path — where the whole
        // point is that it stays held until the promise settles.
        //
        // Rethrown, not swallowed: the error is the call site's, and every
        // other caller of a throwing handler in this app sees it.
        pendingRef.current = false
        throw error
      }

      if (!isThenable(result)) {
        pendingRef.current = false
        restoreFocusOnCloseRef.current = true
        settingOpen(false, event)
        restoreFocusRef.current?.focus()
        return
      }

      setPending(true)
      result.then(
        () => {
          pendingRef.current = false
          setPending(false)
          // The user may have closed the panel while the action was in flight
          // — Escape, Cancel or a click away all close during a pending
          // confirm, as antd's do. The action still runs to completion, but
          // its settle must not reopen the question by re-closing an already
          // closed panel, and must not pull focus back to a trigger the user
          // has left.
          if (dismissedWhilePendingRef.current) {
            dismissedWhilePendingRef.current = false
            return
          }
          restoreFocusOnCloseRef.current = true
          settingOpen(false, event)
          restoreFocusRef.current?.focus()
        },
        () => {
          // antd keeps the popup OPEN when the handler rejects, so the user can
          // read the error and retry. Swallowed rather than re-thrown: antd
          // re-rejects into an unhandled rejection, which every call site here
          // already avoids by handling its own errors.
          pendingRef.current = false
          setPending(false)
          dismissedWhilePendingRef.current = false
        }
      )
    },
    [onConfirm, settingOpen]
  )

  const requestOpen = useCallback(
    (next: boolean, event?: ConfirmEvent) => {
      if (disabled) return
      if (next) {
        restoreFocusRef.current = document.activeElement as HTMLElement | null
        restoreFocusOnCloseRef.current = true
      }
      settingOpen(next, event)
    },
    [disabled, settingOpen]
  )

  /* -----------------------------------------------------------------------
   * THE TRIGGER IS WRAPPED, NOT CLONED — and that is the fix for the worst
   * defect this port shipped.
   *
   * The obvious implementation, and the one this file had, clones the child
   * with an injected `onClick`. That silently assumes the child forwards
   * unknown props down to a DOM node, and TWO live shapes here do not:
   *
   *  • `../Tooltip`. Its shadcn half collects unknown props into `...rest` and
   *    spreads them onto `TooltipContent` — so the injected `onClick` landed on
   *    the tooltip BUBBLE, not on the button. Five call sites pass a Tooltip as
   *    the Popconfirm child (`compliance-documents`, `composting/QuestionRow`,
   *    the view-company cockpit twice, the view-contact cockpit), and with the
   *    design toggle ON every one of those five destructive actions was simply
   *    UNREACHABLE: pressing the button did nothing at all.
   *  • every plain function component, which takes no ref — see the anchor note
   *    above.
   *
   * Fixing it inside `../Tooltip` was considered and rejected: teaching that
   * seam to forward `onClick` to its trigger changes it for ~300 other call
   * sites in order to repair five here. The containment belongs on this side.
   *
   * So the seam asks the child for NOTHING. It renders it untouched inside a
   * `display: contents` span and listens there. `display: contents` because the
   * wrapper must add no box — these triggers sit in table cells, flex rows and
   * antd `Space` children — while staying in the DOM tree, which is all an
   * event needs.
   *
   * That last part is the cost: `display: contents` removes an element from the
   * BOX tree, not from the DOM tree, so a call site rule written with a
   * direct-child combinator or a positional selector across the trigger
   * (`td > button`, `.actions > .ant-btn:first-child`) would stop matching with
   * the toggle on. A scan of `src` found no such rule today — the only
   * `:last-child` rules near a call site are `&:last-child` on styled components
   * in files that host no Popconfirm — so what to watch is a call site ADDING
   * one later. No gate here can see it: jsdom runs no layout.
   *
   * THE HANDLERS ARE CAPTURE-PHASE, deliberately. A bubble-phase listener on
   * the wrapper is defeated by a child that calls `stopPropagation()`, and one
   * live call site (`ViewDashboards`) does exactly that to keep its click off
   * the card underneath. Capture runs ancestor-first, so the confirmation opens
   * before any descendant can stop it. The one visible consequence is ordering:
   * where antd ran the child's own `onClick` first and opened second, this
   * opens first. No call site depends on that order, and the panel is what the
   * user asked for either way.
   *
   * A natively disabled child still opens nothing, because a disabled control
   * dispatches no click at all — which is exactly what antd does too.
   * --------------------------------------------------------------------- */

  const openFromTrigger = (event: ConfirmEvent) => {
    // A SECOND click on the trigger CLOSES, as antd's Popconfirm does.
    // Getting there needs the guard: the trigger is an anchor, not a Radix
    // Trigger, so Radix's dismissable layer reads the pointer-down on it as an
    // OUTSIDE event and has already run `dismiss()` by the time this click is
    // handled. Without the guard the toggle would read the fresh `open ===
    // false` and immediately reopen what the user just closed.
    // (`../Dropdown` avoids this differently — it uses Radix's own Trigger,
    // which suppresses the reopen for it.)
    if (reopenGuardRef.current) {
      reopenGuardRef.current = false
      return
    }
    requestOpen(!open, event)
  }

  // antd binds Escape on the TRIGGER, not the popup, so a user who has not
  // moved focus into the bubble can still dismiss it. Kept, because the popover
  // shape does not trap focus and the trigger is where focus may be.
  const escapeFromTrigger = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape' && open) dismiss()
  }

  // Releases a guard left behind by a pointer-down that never became a click
  // (a press on the trigger, then a drag away). React dispatches this before
  // Radix's own document-level listener, so while the panel is open the guard
  // Radix is about to set is left alone.
  const clearStaleReopenGuard = () => {
    if (!open) reopenGuardRef.current = false
  }

  const trigger = (
    /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
    <span
      data-slot="popconfirm-trigger"
      style={{ display: 'contents' }}
      ref={triggerWrapperRef}
      onClickCapture={openFromTrigger}
      onKeyDownCapture={escapeFromTrigger}
      onPointerDownCapture={clearStaleReopenGuard}
    >
      {children}
    </span>
  )

  const cancelButton = showCancel ? (
    <Button
      htmlType="button"
      size="small"
      data-slot="popconfirm-cancel"
      {...(cancelButtonProps as ButtonProps)}
      onClick={handleCancel}
    >
      {cancelText ?? localeConfig?.cancelText}
    </Button>
  ) : null

  const okButton = (
    <Button
      htmlType="button"
      size="small"
      type={okType === 'danger' ? 'primary' : okType}
      danger={okType === 'danger'}
      loading={pending}
      data-slot="popconfirm-ok"
      // AFTER `loading`, exactly as antd spreads `buttonProps` after its own:
      // 10 call sites drive a row-scoped `loading` and must keep winning.
      {...(okButtonProps as ButtonProps)}
      onClick={runConfirm}
    >
      {okText ?? localeConfig?.okText}
    </Button>
  )

  const buttons = (
    <div data-slot="popconfirm-buttons" className="mt-2 flex justify-end gap-2">
      {cancelButton}
      {okButton}
    </div>
  )

  /**
   * A CLICK INSIDE A CONFIRMATION BELONGS TO THE CONFIRMATION. Found in the
   * browser, not by a test, on the Insights dashboards grid: clicking the
   * modal's BACKDROP navigated to the dashboard the confirm was guarding.
   *
   * The cause is React's synthetic event system, and it applies to BOTH shapes.
   * Radix portals the panel to `document.body`, but a portal re-parents the
   * DOM, NOT the React tree — a synthetic event raised inside it bubbles up the
   * tree the panel was DECLARED in. Every one of these confirms is declared
   * inside the row or card it acts on, and several of those (the dashboards
   * grid, the compensation-models table) carry their own `onClick`. So a click
   * on the panel — its backdrop, its padding, its buttons — reached that
   * handler and navigated away mid-confirmation.
   *
   * antd hit the same wall from the other side: it has no backdrop, so only its
   * buttons were exposed, which is why two call sites here already pass
   * `stopPropagation` by hand (`ViewDashboards`' `onCancel`, its trigger's
   * `onClick`). Stopping it once, here, is what makes those call-site patches
   * unnecessary rather than load-bearing — and it costs nothing on the antd
   * rendering, which never reaches this file.
   *
   * `onPopupClick` still runs first: it is antd's own hook for a call site that
   * wants panel clicks, and it keeps whatever the site does with them.
   */
  const containClick = (event: React.MouseEvent<HTMLElement>) => {
    onPopupClick?.(event)
    event.stopPropagation()
  }

  /**
   * The containment BOUNDARY for the modal shape, and why a handler on the
   * panel alone is not enough — this cost two browser passes to find.
   *
   * `AlertDialogContent` renders the overlay as a SIBLING of the panel inside
   * the portal, so a click on the backdrop never passes through the panel and
   * `containClick` above never runs for it. Since React bubbling follows the
   * React tree and not the DOM, that backdrop click went straight from the
   * portal to the card's `onClick` — on the Insights dashboards grid it
   * navigated to the dashboard the confirm was guarding.
   *
   * The wrapper below is the nearest common React ancestor of the overlay AND
   * the panel, so it catches both. It is written INLINE rather than as a small
   * component: a component declared inside this function body is a new type on
   * every render, which would unmount and remount the whole dialog — and its
   * open state with it — every time the parent re-rendered.
   *
   * `display: contents` because it must add no box: it sits beside the trigger
   * in the DOM and would otherwise be an inline element inside table cells,
   * flex rows and antd `Space` children at every one of the modal call sites.
   */

  /**
   * Focus Cancel on open. `requestAnimationFrame` is not needed — the handler
   * runs after Radix has mounted the content — but the node may be absent when
   * `showCancel` is false, in which case OK takes it.
   */
  const focusDefaultAction = (event: Event) => {
    event.preventDefault()
    const root = event.currentTarget as HTMLElement | null
    const target =
      root?.querySelector<HTMLElement>('[data-slot="popconfirm-cancel"]') ??
      root?.querySelector<HTMLElement>('[data-slot="popconfirm-ok"]')
    target?.focus()
  }

  if (modal) {
    return (
      <>
        {trigger}
        {/* This element is NOT interactive and must not become so: it handles
            no user intent, it only stops a synthetic click escaping the portal
            into the row that declared it (see the note above). Giving it a
            role or a key handler would put a phantom control in the tab order
            and in the accessibility tree beside every modal confirm. */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <span
          data-slot="popconfirm-boundary"
          style={{ display: 'contents' }}
          onClick={containClick}
        >
          <AlertDialog open={open}>
            <AlertDialogContent
              {...omitTransient(rest as Record<string, unknown>)}
              // NO `aria-describedby` override here, and that is checked
              // rather than assumed. Three modal sites pass no description
              // (`RuleEditorCards`, the omnisearch reindex, the holiday bulk
              // accept), and older Radix pointed the attribute at a Description
              // id that nothing rendered — a dangling reference plus a console
              // warning. The version this repo pins does not: `react-dialog`
              // emits `aria-describedby` only when `descriptionPresent`
              // (dist/index.mjs:232), so the attribute is already absent and
              // nothing is logged. Verified on radix-ui 1.6.7, and pinned from
              // both sides in `../__tests__/popconfirm-seam-parity.test.tsx`.
              // A Radix bump that regresses this is what those two cases catch.
              data-slot="popconfirm"
              data-shape="modal"
              className={overlayClassName ?? className}
              style={overlayStyle as React.CSSProperties}
              onClick={containClick}
              onOpenAutoFocus={focusDefaultAction}
              onCloseAutoFocus={(event) => {
                event.preventDefault()
                if (restoreFocusOnCloseRef.current) {
                  restoreFocusRef.current?.focus()
                }
              }}
              onEscapeKeyDown={(event) => {
                event.preventDefault()
                dismiss()
              }}
            >
              <Message
                icon={icon}
                title={title}
                description={description}
                titleId={titleId}
                descriptionId={descriptionId}
                renderTitle={(node) => (
                  <AlertDialogTitle data-slot="popconfirm-title">
                    {node}
                  </AlertDialogTitle>
                )}
                renderDescription={(node) => (
                  <AlertDialogDescription
                    asChild
                    data-slot="popconfirm-description"
                  >
                    <div>{node}</div>
                  </AlertDialogDescription>
                )}
              />
              {buttons}
            </AlertDialogContent>
          </AlertDialog>
        </span>
      </>
    )
  }

  const { side, align } = toRadixSideAlign(
    placement as AntdPlacement | undefined,
    // antd's Popconfirm default, which differs from Popover's `bottomLeft`.
    'top'
  )

  // antd resolves `getPopupContainer` lazily, once the popup opens and the
  // trigger node exists; here it can run on the first render with no node yet,
  // and calling it with `null` crashes a consumer written as
  // `(trigger) => trigger.parentElement`. Same shape as `../Dropdown`.
  const container = (() => {
    if (typeof getPopupContainer !== 'function') return undefined
    return anchorNode ? getPopupContainer(anchorNode) ?? undefined : undefined
  })()

  return (
    <Popover
      open={open}
      onOpenChange={(next) => requestOpen(next)}
      // NOT the default falling through — see the header. A modal Radix
      // popover removes the page scrollbar, which shifts a table sideways
      // every time a row confirm opens.
      modal={false}
    >
      {/* A VIRTUAL anchor: `PopoverAnchor` renders nothing at all when it is
          given one, so the trigger below is the seam's own markup and carries
          none of Radix's anchor attributes. The node comes from the DOM (see
          the `anchorNode` effect above) rather than from a ref the child may
          never forward. Radix re-reads `virtualRef.current` on every commit,
          so a state change here is what re-anchors the panel. */}
      <PopoverAnchor virtualRef={{ current: anchorNode }} />
      {trigger}
      <PopoverContent
        {...omitTransient(rest as Record<string, unknown>)}
        data-slot="popconfirm"
        data-shape="popover"
        // A confirmation demands a response; `alertdialog` is the role for it,
        // and it is what antd's own `Modal.confirm` renders. Radix's Popover
        // defaults to `dialog`.
        role="alertdialog"
        aria-labelledby={title != null && title !== '' ? titleId : undefined}
        aria-describedby={hasDescription ? descriptionId : undefined}
        side={side}
        align={align}
        className={`min-w-[177px] max-w-[300px] p-3 ${
          overlayClassName ?? className ?? ''
        }`}
        style={overlayStyle as React.CSSProperties}
        container={container}
        onClick={containClick}
        onOpenAutoFocus={focusDefaultAction}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          if (restoreFocusOnCloseRef.current) restoreFocusRef.current?.focus()
        }}
        // Every one of the three below is a DISMISSAL and none of them may
        // confirm. They are listed separately rather than left to Radix's
        // defaults so the safety property is readable in one place.
        onEscapeKeyDown={(event) => {
          event.preventDefault()
          dismiss()
        }}
        // Neither of the two OUTSIDE paths below restores focus, and that is
        // the difference between them and Escape/Cancel/confirm: the user has
        // already sent focus somewhere — the next control they tabbed to, or
        // wherever they clicked — so pulling it back to the trigger would
        // swallow that Tab and can scroll the page back to a trigger they
        // scrolled away from. Escape and Cancel are the deliberate ways to back
        // out, and those DO return focus to the trigger.
        onPointerDownOutside={(event) => {
          event.preventDefault()
          // A pointer-down ON THE TRIGGER is "outside" as far as Radix is
          // concerned — the trigger is an anchor, not a Radix Trigger — but to
          // the user it is the second half of a toggle. Mark it so the click
          // that follows closes instead of reopening. See `openFromTrigger`.
          const target = event.detail.originalEvent.target as Node | null
          if (target && triggerWrapperRef.current?.contains(target)) {
            reopenGuardRef.current = true
          }
          dismiss(undefined, false)
        }}
        onFocusOutside={(event) => {
          event.preventDefault()
          dismiss(undefined, false)
        }}
      >
        <Message
          icon={icon}
          title={title}
          description={description}
          titleId={titleId}
          descriptionId={descriptionId}
          renderTitle={(node, id) => (
            <div
              id={id}
              data-slot="popconfirm-title"
              className="text-sm font-semibold"
            >
              {node}
            </div>
          )}
          renderDescription={(node, id) => (
            <div id={id} data-slot="popconfirm-description" className="text-sm">
              {node}
            </div>
          )}
        />
        {buttons}
      </PopoverContent>
    </Popover>
  )
}

PopconfirmShadcn.displayName = 'Popconfirm'
